import type { RiskScoreHistoryRow } from '@risksmart-app/drizzle/src/types';
import { useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { useTRPC } from 'src/utils/trpc';

import { useErrorNotification } from '@/hooks/useErrorNotification';

type UseGetRiskScoreHistoryArgs = {
  riskId: string;
  isSkipped?: boolean;
};

type UseGetRiskScoreHistoryResult = {
  data: RiskScoreHistoryRow[] | undefined;
  loading: boolean;
  error: TRPCClientErrorLike<{ transformer: true; errorShape: unknown }> | null;
};

/**
 * Persisted risk-score history from `risk_score_audit` (RSP-6566, phase 2).
 *
 * Unlike most read hooks this is NOT built on `createQueryHook`: the audit
 * table is exposed ONLY through the tRPC endpoint added in phase 1 (RSP-6565)
 * — there is no GraphQL equivalent to fall back to — so the hook always calls
 * tRPC and is not gated on the `trpc` feature flag. RLS is enforced server-side
 * through the owning `risk`; a risk the caller cannot read returns `[]`.
 *
 * Rows are returned oldest → newest (`ModifiedAtTimestamp ASC`). The full
 * history is fetched (no date range / limit) — the tab renders every point.
 */
export const useGetRiskScoreHistory = ({
  riskId,
  isSkipped,
}: UseGetRiskScoreHistoryArgs): UseGetRiskScoreHistoryResult => {
  const trpc = useTRPC();

  // Let the generics infer from `queryOptions` — passing explicit type args
  // conflicts with the options' own queryFn typing (TS2769/TS2322).
  const { data, isLoading, error } = useQuery({
    ...trpc.frontend.risk.riskScoreHistory.queryOptions({ riskId }),
    enabled: !isSkipped,
  });

  useErrorNotification(error, true);

  return { data, loading: isLoading, error };
};
